<template>
  <div ref="htmlContainerRef" class="form-html-element-wrapper">
    <label
      :style="labelStyle"
      class="form-label d-flex align-items-center gap-2 p-0"
      :class="element.name"
      v-if="!isPreview && !element.settings.isHtmlRendered"
    >
      <i
        :style="{ color: labelStyle.color }"
        class="fa fa-palette fs-6 fw-bol"
      ></i>
      <span :style="labelStyle" class="text-start text-break fs-6 fw-bol">
        {{ element.label }}
        <span v-if="showRequiredMark" class="required-star">*</span>
      </span>
    </label>
    <div
      class="html-render"
      v-if="element.settings.isHtmlRendered && !isPreview"
      v-html="element.value"
    ></div>
  </div>
</template>

<script>
const formHtmlHeadLinkRefCount = new Map();
const formHtmlHeadScriptRefCount = new Map();

export default {
  name: 'FormHtmlElement',
  data() {
    return {
      managedHeadLinks: [],
      managedHeadScripts: [],
    };
  },
  props: {
    element: {
      type: Object,
      required: true,
    },
    labelStyle: { type: Object, default: () => {} },
    isPreview: { type: Boolean, default: false },
  },
  computed: {
    showRequiredMark() {
      return (
        !!this.element?.settings?.required &&
        !this.element?.settings?.disabled &&
        this.element?.type !== 'html' &&
        String(this.element?.label || '').trim().length > 0
      );
    },
  },
  methods: {
    waitForAnimationFrame() {
      return new Promise((resolve) => {
        requestAnimationFrame(() => resolve());
      });
    },
    async waitForScriptExecutionWindow() {
      await this.$nextTick();
      await this.waitForAnimationFrame();
      await this.waitForAnimationFrame();
    },
    findManagedHeadNode(tagName, attrName, attrValue) {
      const nodes = document.head.querySelectorAll(`${tagName}[${attrName}]`);
      return Array.from(nodes).find(
        (node) => node.getAttribute(attrName) === attrValue
      );
    },
    registerHeadAsset(type, value) {
      if (!value) return;
      if (type === 'link') {
        if (!this.managedHeadLinks.includes(value)) {
          this.managedHeadLinks.push(value);
          formHtmlHeadLinkRefCount.set(
            value,
            (formHtmlHeadLinkRefCount.get(value) || 0) + 1
          );
        }
        return;
      }
      if (!this.managedHeadScripts.includes(value)) {
        this.managedHeadScripts.push(value);
        formHtmlHeadScriptRefCount.set(
          value,
          (formHtmlHeadScriptRefCount.get(value) || 0) + 1
        );
      }
    },
    cleanupHeadAssets() {
      this.managedHeadLinks.forEach((href) => {
        const nextCount = (formHtmlHeadLinkRefCount.get(href) || 1) - 1;
        if (nextCount <= 0) {
          formHtmlHeadLinkRefCount.delete(href);
          const linkNode = this.findManagedHeadNode(
            'link',
            'data-form-html-href',
            href
          );
          if (linkNode) linkNode.remove();
        } else {
          formHtmlHeadLinkRefCount.set(href, nextCount);
        }
      });
      this.managedHeadLinks = [];

      this.managedHeadScripts.forEach((src) => {
        const nextCount = (formHtmlHeadScriptRefCount.get(src) || 1) - 1;
        if (nextCount <= 0) {
          formHtmlHeadScriptRefCount.delete(src);
          const scriptNode = this.findManagedHeadNode(
            'script',
            'data-form-html-src',
            src
          );
          if (scriptNode) scriptNode.remove();
        } else {
          formHtmlHeadScriptRefCount.set(src, nextCount);
        }
      });
      this.managedHeadScripts = [];
    },
    async renderHtmlContent() {
      try {
        if (!this.element?.value) return;

        const container = this.$refs.htmlContainerRef;
        if (!container) return;

        this.cleanupHeadAssets();

        // Clear previous content
        container.innerHTML = '';

        // Parse HTML string
        const parser = new DOMParser();
        const doc = parser.parseFromString(this.element.value, 'text/html');

        // --- STEP 0: Handle <link rel="stylesheet"> tags (preview only) ---
        if (this.isPreview) {
          doc.querySelectorAll('link[rel="stylesheet"]').forEach((linkTag) => {
            const href = linkTag.getAttribute('href');
            if (!href) return;
            let existing = this.findManagedHeadNode(
              'link',
              'data-form-html-href',
              href
            );
            if (!existing) {
              const newLink = document.createElement('link');
              for (let attr of linkTag.attributes) {
                newLink.setAttribute(attr.name, attr.value);
              }
              newLink.setAttribute('data-form-html-href', href);
              document.head.appendChild(newLink);
              existing = newLink;
            }
            if (existing) this.registerHeadAsset('link', href);
          });
        }

        // --- STEP 1: Extract <script> tags first (from anywhere in the DOM) ---
        const extractedScripts = [];
        doc.querySelectorAll('script').forEach((scriptTag) => {
          extractedScripts.push(scriptTag);
          scriptTag.remove(); // remove script from its parent (even inside divs)
        });

        // --- STEP 2: Handle <style> tags (append first) ---
        const styleTags = doc.querySelectorAll('style');
        styleTags.forEach((styleTag) => {
          const newStyle = document.createElement('style');
          newStyle.textContent = styleTag.textContent;
          container.appendChild(newStyle);
          styleTag.remove();
        });

        // --- STEP 3: Append remaining HTML content (now without scripts) ---
        Array.from(doc.body.childNodes).forEach((node) => {
          container.appendChild(node.cloneNode(true));
        });

        await this.waitForScriptExecutionWindow();

        // --- STEP 4: Append extracted scripts (last, to execute them) ---
        const ensureHeadScript = (oldScript) => {
          const src = oldScript.getAttribute('src');
          if (!src) return false;

          const existing = this.findManagedHeadNode(
            'script',
            'data-form-html-src',
            src
          );
          if (existing) {
            const alreadyLoaded =
              existing.getAttribute('data-form-html-loaded') === 'true';
            if (alreadyLoaded) return Promise.resolve(true);
            return new Promise((resolve) => {
              existing.addEventListener('load', () => resolve(true), {
                once: true,
              });
              existing.addEventListener('error', () => resolve(false), {
                once: true,
              });
            });
          }

          const headScript = document.createElement('script');
          for (let attr of oldScript.attributes) {
            headScript.setAttribute(attr.name, attr.value);
          }
          headScript.setAttribute('data-form-html-src', src);
          return new Promise((resolve) => {
            headScript.addEventListener(
              'load',
              () => {
                headScript.setAttribute('data-form-html-loaded', 'true');
                resolve(true);
              },
              { once: true }
            );
            headScript.addEventListener('error', () => resolve(false), {
              once: true,
            });
            document.head.appendChild(headScript);
          });
        };

        // --- STEP 4a: Ensure external scripts are loaded first (preview only) ---
        if (this.isPreview) {
          const uniqueSrc = new Set();
          const loadPromises = extractedScripts
            .filter((s) => s.getAttribute('src'))
            .map((s) => {
              const src = s.getAttribute('src');
              if (!src) return null;
              if (!uniqueSrc.has(src)) {
                this.registerHeadAsset('script', src);
                uniqueSrc.add(src);
              }
              return ensureHeadScript(s);
            })
            .filter(Boolean);
          if (loadPromises.length) {
            await Promise.all(loadPromises);
          }
        }

        // --- STEP 4b: Append inline scripts (last, to execute them) ---
        extractedScripts.forEach((oldScript) => {
          const newScript = document.createElement('script');

          // Copy over attributes like src, type, etc.
          for (let attr of oldScript.attributes) {
            newScript.setAttribute(attr.name, attr.value);
          }

          // In preview, move external scripts to <head> to load once.
          if (this.isPreview && oldScript.getAttribute('src')) {
            return;
          }

          // Copy inline script content.
          // Wrap classic scripts to avoid re-declare errors on re-render.
          if (oldScript.textContent) {
            const scriptType = oldScript.getAttribute('type') || '';
            if (scriptType.toLowerCase() === 'module') {
              newScript.textContent = oldScript.textContent;
            } else {
              newScript.textContent = `(function(){\n${oldScript.textContent}\n})();`;
            }
          }

          // Append (executes when inserted)
          container.appendChild(newScript);
        });
      } catch (err) {
        console.error('Failed to render dynamic content:', err);
      }
    },
  },
  mounted() {
    if (this.isPreview) this.renderHtmlContent();
  },
  beforeDestroy() {
    this.cleanupHeadAssets();
  },
};
</script>

<style scoped>
.form-html-element-wrapper .form-label {
  margin: 0px;
}
.form-html-element-wrapper .form-label .html-render {
  margin-top: 18px;
}

.form-html-element-wrapper .form-label i {
  color: #000000;
}
.form-html-element-wrapper .form-label span {
  font-family: 'OpenSans-Bold';
  color: #000000;
  line-height: 27px;
  letter-spacing: -0.02em;
}
.form-html-element-wrapper .form-label span.required-star {
  color: #ff0000;
  margin-left: 4px;
  display: inline-block;
  line-height: 1;
  transform: scale(0.8);
  transform-origin: left center;
  vertical-align: baseline;
}
</style>
